/**
 * PlacementOverlay - Full-screen immersive overlay for bin/shelf placement
 *
 * Replaces BinConfirmationModal and PackingShelfConfirmationModal with a single,
 * large, impossible-to-miss overlay. Existing assignments can preview while saving.
 *
 * Design:
 * - Bin variant: Dark blue full-screen with massive bin label
 * - Bench variant (BLU-197): Dark indigo full-screen with massive bench label
 * - Shelf variant: Dark green full-screen with "PACKING BENCH"
 * - Error: Inline error with auto-clear
 *
 * Dismissal:
 * - Scan matching QR code (bin/bench must match expected label, shelf must be PACKING)
 * - Click manual "Placed in Bin" / "Done — Next Garment" button (fallback for broken QR)
 * - Press or hold Enter on an empty scanner field for the same manual confirmation
 * - Scanning wrong bin/bench shows error with expected label
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogDescription,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { QRScannerInput } from './QRScannerInput';
import { playErrorBeep, playSuccessBeep } from '@/lib/sounds';
import { cn } from '@/lib/utils';
import { Box, Package, AlertCircle, AlertTriangle, CheckCircle2, Loader2, LayoutPanelTop } from 'lucide-react';
import { detectQRType, extractBinLabel, extractBenchLabel } from '@/lib/qr-utils';

interface PlacementOverlayProps {
  open: boolean;
  /** BLU-197: 'bench' = labelled work-bench spot (B2B/large orders). */
  variant: 'bin' | 'shelf' | 'bench';
  binLabel: string;
  onDismiss: () => void;
  onUndo?: () => Promise<void>;
  isUndoing?: boolean;
  isSaving?: boolean;
  isConfirmed?: boolean;
  /** Catcher opt-in; the shared picking overlay retains scan-only Enter for containers. */
  allowManualEnter?: boolean;
  /** Yield keyboard ownership when a save/undo error dialog is showing. */
  paused?: boolean;
  /** The server's saved destination differs from what was previewed — needs a fresh confirmation. */
  destinationChanged?: boolean;
}

export function PlacementOverlay({
  open,
  variant,
  binLabel,
  onDismiss,
  onUndo,
  isUndoing,
  isSaving = false,
  isConfirmed = false,
  allowManualEnter = false,
  paused = false,
  destinationChanged = false,
}: PlacementOverlayProps) {
  const { t } = useTranslation('production');
  const [error, setError] = useState<string | null>(null);
  const isDismissingRef = useRef(false);
  const isUndoRequestedRef = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cooldown after scan error to block trailing Enter from USB scanners (CR+LF = 2 keystrokes)
  const scanCooldownUntilRef = useRef(0);
  const blockedEnterRef = useRef(false);
  // A held Enter only confirms once armed by a genuine keyup — otherwise a
  // repeat carried over from a previous confirm (or across a destination
  // change) could auto-confirm without the operator ever releasing the key.
  const enterArmedRef = useRef(false);

  // Auto-clear error after 5 seconds (warehouse workers need time to read)
  useEffect(() => {
    if (!error) return;
    errorTimerRef.current = setTimeout(() => {
      setError(null);
    }, 5000);
    return () => {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    };
  }, [error]);

  // Reset all state when overlay opens/closes + clear dangling timer
  useEffect(() => {
    setError(null);
    isDismissingRef.current = false;
    scanCooldownUntilRef.current = 0;
    blockedEnterRef.current = false;
    enterArmedRef.current = false;
    if (errorTimerRef.current) {
      clearTimeout(errorTimerRef.current);
      errorTimerRef.current = null;
    }
  }, [open, variant, binLabel]);

  // Guarded dismiss to prevent double-scan race from USB scanners
  // Uses ref for synchronous check - useState would be stale within the same render cycle
  const guardedDismiss = useCallback(async () => {
    if (!open || paused || isUndoing || isUndoRequestedRef.current || isConfirmed || isDismissingRef.current) return;
    isDismissingRef.current = true;
    // A queued confirmation is not a successful save. CatcherPage beeps when it resolves.
    if (!isSaving) playSuccessBeep();
    try {
      await onDismiss();
    } finally {
      // A rejected onDismiss (e.g. Picking's confirmPapPlacement) must not leave
      // the overlay permanently un-dismissable — isConfirmed still gates a
      // second real confirm on the Catcher path.
      isDismissingRef.current = false;
    }
  }, [open, paused, isUndoing, isConfirmed, isSaving, onDismiss]);

  const handleInvalidQR = useCallback((message: string) => {
    scanCooldownUntilRef.current = Date.now() + 300;
    blockedEnterRef.current = true;
    setError(message);
    playErrorBeep();
  }, []);

  const handleScan = useCallback(
    (qrCode: string) => {
      if (paused || isDismissingRef.current || isUndoRequestedRef.current || isUndoing) return;
      if (isConfirmed) {
        // Confirmed but still saving — the scanner stays focused (F1), so make a
        // stray scan audible rather than silently dropping it.
        playErrorBeep();
        return;
      }

      // Block trailing Enter from USB scanner for 300ms after any scan
      scanCooldownUntilRef.current = Date.now() + 300;
      blockedEnterRef.current = true;

      // Clear any previous error
      setError(null);

      const qrType = detectQRType(qrCode);

      if (variant === 'bin') {
        if (qrType === 'bin') {
          const scannedLabel = extractBinLabel(qrCode);

          if (scannedLabel !== binLabel) {
            setError(t('qr.wrongBin', { binLabel }));
            playErrorBeep();
          } else {
            guardedDismiss();
          }
          return;
        }

        // Specific error messages for wrong QR types
        if (qrType === 'shelf') {
          setError(t('qr.wrongTypeBinExpected', { type: t('qr.shelfQrLabel') }));
        } else if (qrType === 'garment') {
          setError(t('qr.wrongTypeBinExpected', { type: t('qr.garmentQrLabel') }));
        } else {
          setError(t('qr.wrongTypeBinExpected', { type: '' }));
        }
        playErrorBeep();
        return;
      }

      if (variant === 'bench') {
        if (qrType === 'bench') {
          const scannedLabel = extractBenchLabel(qrCode);

          if (scannedLabel !== binLabel) {
            setError(t('qr.wrongBench', { benchLabel: binLabel }));
            playErrorBeep();
          } else {
            guardedDismiss();
          }
          return;
        }

        // Specific error messages for wrong QR types (expecting a bench)
        if (qrType === 'bin') {
          setError(t('qr.wrongTypeBenchExpected', { type: t('qr.binQrLabel') }));
        } else if (qrType === 'shelf') {
          setError(t('qr.wrongTypeBenchExpected', { type: t('qr.shelfQrLabel') }));
        } else if (qrType === 'garment') {
          setError(t('qr.wrongTypeBenchExpected', { type: t('qr.garmentQrLabel') }));
        } else {
          setError(t('qr.wrongTypeBenchExpected', { type: '' }));
        }
        playErrorBeep();
        return;
      }

      if (variant === 'shelf') {
        if (qrCode === 'THP://SHELF:PACKING') {
          guardedDismiss();
          return;
        }

        // Specific error messages for wrong QR types
        if (qrType === 'bin') {
          setError(t('qr.wrongTypeShelfExpected', { type: t('qr.binQrLabel') }));
        } else if (qrType === 'garment') {
          setError(t('qr.wrongTypeShelfExpected', { type: t('qr.garmentQrLabel') }));
        } else {
          setError(t('qr.wrongTypeShelfExpected', { type: '' }));
        }
        playErrorBeep();
        return;
      }
    },
    [variant, binLabel, guardedDismiss, paused, isUndoing, isConfirmed, t],
  );

  const handleManualDismiss = useCallback(() => {
    guardedDismiss();
  }, [guardedDismiss]);

  const handleUndo = useCallback(async () => {
    if (!onUndo || paused || isSaving || isConfirmed || isUndoing || isUndoRequestedRef.current || isDismissingRef.current) return;
    isUndoRequestedRef.current = true;
    try {
      await onUndo();
    } finally {
      isUndoRequestedRef.current = false;
    }
  }, [onUndo, paused, isSaving, isConfirmed, isUndoing]);

  // Keyboard hotkeys: Enter = dismiss, Backspace = undo
  useEffect(() => {
    if (!open || paused) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.altKey || e.metaKey || e.shiftKey || e.isComposing) return;
      const scanner = contentRef.current?.querySelector<HTMLInputElement>('[data-scanner-input]');

      if (e.key === 'Enter') {
        if (variant !== 'shelf' && !allowManualEnter) return;
        // A scan terminator must reach QRScannerInput for validation, even if the
        // payload is incomplete. Only an empty field means manual confirmation.
        if (scanner?.value.trim()) {
          blockedEnterRef.current = true;
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        // Don't let CR+LF, a wrong scan's trailing Enter, or its held repeats
        // turn a validation failure into manual confirmation.
        if (error || Date.now() < scanCooldownUntilRef.current) {
          blockedEnterRef.current = true;
          return;
        }
        if (e.repeat && (!enterArmedRef.current || blockedEnterRef.current)) return;
        blockedEnterRef.current = false;
        if (e.target instanceof Element && e.target.closest('[data-placement-undo]')) {
          void handleUndo();
          return;
        }
        guardedDismiss();
      } else if (e.key === 'Backspace' && onUndo) {
        if (scanner?.value.trim()) return; // Preserve editing an incomplete scan.
        e.preventDefault();
        e.stopPropagation();
        void handleUndo();
      }
    };

    // A held Enter only confirms once the physical key has actually been
    // released and pressed again since open (or since the destination
    // changed) — see enterArmedRef above.
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Enter') enterArmedRef.current = true;
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('keyup', handleKeyUp, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('keyup', handleKeyUp, true);
    };
  }, [open, paused, error, variant, allowManualEnter, guardedDismiss, onUndo, handleUndo]);

  if (!open) return null;

  const isBin = variant === 'bin';
  const isBench = variant === 'bench';
  // Bin and bench are both labelled spots confirmed by a validated QR scan.
  const requiresScan = isBin || isBench;

  // Overlay Color Language: Blue = bin, Indigo = bench (BLU-197), Green = shelf.
  // Bench gets its own semantic hue so operators can tell a bench placement from
  // a bin placement across the room — distinct, but still a "container" cool tone.
  const overlayBg = isBin
    ? 'bg-gradient-to-b from-blue-950 via-blue-900 to-blue-950'
    : isBench
      ? 'bg-gradient-to-b from-indigo-950 via-indigo-900 to-indigo-950'
      : 'bg-gradient-to-b from-green-950 via-green-900 to-green-950';

  // Manual Enter is a Catcher-only opt-in for bin/bench (shelf always supports
  // it) — mention it in the aria copy only when it's actually available.
  const ariaTitle = isBin
    ? t(allowManualEnter ? 'placement.ariaPlaceBinEnter' : 'placement.ariaPlaceBin', { binLabel })
    : isBench
      ? t(allowManualEnter ? 'placement.ariaPlaceBenchEnter' : 'placement.ariaPlaceBench', { benchLabel: binLabel })
      : t('placement.ariaPlaceShelf');
  const scanConfirmText = isBin
    ? t(allowManualEnter ? 'placement.scanBinConfirmEnter' : 'placement.scanBinConfirm', { binLabel })
    : isBench
      ? t(allowManualEnter ? 'placement.scanBenchConfirmEnter' : 'placement.scanBenchConfirm', { benchLabel: binLabel })
      : t('placement.doneNextGarment');

  return (
    <AlertDialog open={open}>
      <AlertDialogPortal>
        {/* Custom overlay - full-screen colored background with fade-in */}
        <AlertDialogOverlay
          className={cn(
            'fixed inset-0 z-50',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-200',
            overlayBg,
          )}
        />
        <AlertDialogContent
          ref={contentRef}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            // Radix runs close autofocus after unmount, when the station's
            // scanner is enabled again. Do not restore the old Accept button.
            document.querySelector<HTMLInputElement>('[data-scanner-input]:not(:disabled)')?.focus({ preventScroll: true });
          }}
          className={cn(
            'fixed inset-0 z-50 flex items-center justify-center',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:duration-200',
            'border-0 bg-transparent p-4 shadow-none max-w-none w-auto',
            'translate-x-0 translate-y-0 left-0 top-0',
          )}
        >
          <AlertDialogTitle className="sr-only">{ariaTitle}</AlertDialogTitle>
          <AlertDialogDescription className="sr-only">{scanConfirmText}</AlertDialogDescription>
          <div className="w-full max-w-4xl mx-auto flex flex-col items-center gap-6">
            {isBin ? (
              <BinPlacementState binLabel={binLabel} />
            ) : isBench ? (
              <BenchPlacementState benchLabel={binLabel} />
            ) : (
              <ShelfPlacementState />
            )}

            {/* Error display - large and prominent for warehouse visibility */}
            {error && (
              <div className="w-full flex items-center gap-4 px-6 py-5 rounded-xl bg-red-950/90 border-2 border-red-500/70 animate-pulse-subtle" role="alert">
                <AlertCircle className="h-10 w-10 text-red-400 shrink-0" />
                <p className="text-2xl font-bold text-red-200">{error}</p>
              </div>
            )}

            {/* The server's saved destination differs from what was previewed. */}
            {destinationChanged && !error && (
              <div className="w-full flex items-center gap-4 px-6 py-5 rounded-xl bg-amber-950/90 border-2 border-amber-500/70">
                <AlertTriangle className="h-10 w-10 text-amber-400 shrink-0" aria-hidden="true" />
                <p className="text-2xl font-bold text-amber-200">{t('placement.binChanged', { label: binLabel })}</p>
              </div>
            )}

            {/* Scanner input - shown for bin + bench variants (shelf doesn't use QR codes) */}
            {requiresScan && (
              <div className="w-full rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-4">
                <QRScannerInput
                  onScan={handleScan}
                  onInvalidQR={handleInvalidQR}
                  autoFocus
                  soundEnabled={false}
                  disabled={paused || isUndoing}
                  placeholder={isBench ? t('scanBenchPlaceholder') : t('scanBinPlaceholder')}
                  ariaLabel={scanConfirmText}
                />
              </div>
            )}

            {/* Manual dismiss button - large for gloved hands. Content (not position)
                swaps for confirmed-while-saving / saving-without-confirmation so
                nothing shifts underneath it. */}
            <Button
              variant="ghost"
              size="lg"
              className={cn(
                'w-full h-20 text-2xl font-semibold rounded-xl',
                isBin && 'text-blue-300/70 hover:text-blue-100 hover:bg-blue-800/30',
                isBench && 'text-indigo-300/70 hover:text-indigo-100 hover:bg-indigo-800/30',
                !requiresScan && 'bg-green-600 text-white hover:text-white hover:bg-green-600 active:bg-green-700 active:text-white',
                isConfirmed && isSaving && 'bg-emerald-600 text-white hover:bg-emerald-600 hover:text-white',
                !isConfirmed && isSaving && 'bg-amber-600/90 text-white hover:bg-amber-600/90 hover:text-white',
              )}
              onClick={handleManualDismiss}
              disabled={paused || isUndoing || isConfirmed}
              aria-busy={isConfirmed && isSaving ? true : undefined}
            >
              {isConfirmed && isSaving ? (
                <>
                  <CheckCircle2 className="h-8 w-8" aria-hidden="true" />
                  {t('placement.confirmedSaving')}
                </>
              ) : !isConfirmed && isSaving ? (
                <>
                  <Loader2 className="h-8 w-8 animate-spin" aria-hidden="true" />
                  {t('placement.savingPlacement')}
                </>
              ) : (
                <>
                  {isBin
                    ? t('placement.placedInBin')
                    : isBench
                      ? t('placement.placedOnBench')
                      : t('placement.doneNextGarment')}
                  {(variant === 'shelf' || allowManualEnter) && (
                    <kbd className={cn(
                      'ml-2 text-xs font-normal px-1.5 py-0.5 rounded',
                      isBin && 'bg-blue-800/50 text-blue-300/60',
                      isBench && 'bg-indigo-800/50 text-indigo-300/60',
                      !requiresScan && 'bg-white/15 text-white/70',
                    )}>Enter</kbd>
                  )}
                </>
              )}
            </Button>

            {/* Undo button - secondary action below placement (only shown at catcher station) */}
            {onUndo && (
              <Button
                variant="ghost"
                size="lg"
                className="w-full h-16 text-lg text-white/70 hover:text-white hover:bg-transparent"
                data-placement-undo
                onClick={handleUndo}
                disabled={paused || isUndoing || isSaving || isConfirmed || isDismissingRef.current}
              >
                {isUndoing ? t('undoing') : t('placement.undo')}
                {!isUndoing && (
                  <kbd className="ml-2 text-xs font-normal px-1.5 py-0.5 rounded bg-white/10 text-white/40">Backspace</kbd>
                )}
              </Button>
            )}
          </div>

          {/* Single ARIA live region for the dialog — error, destination-change,
              saving/confirmed state, and the default placement prompt all fold
              in here rather than each owning a separate role="status". */}
          <div role="status" aria-live="polite" className="sr-only">
            {error
              ? `Error: ${error}`
              : destinationChanged
                ? t('placement.binChanged', { label: binLabel })
                : isConfirmed && isSaving
                  ? t('placement.confirmedSaving')
                  : isSaving
                    ? t('placement.savingPlacement')
                    : ariaTitle}
          </div>
        </AlertDialogContent>
      </AlertDialogPortal>
    </AlertDialog>
  );
}

function BinPlacementState({ binLabel }: { binLabel: string }) {
  const { t } = useTranslation('production');
  return (
    <div className="text-center space-y-3">
      <div className="flex items-center justify-center gap-3">
        <Box className="h-7 w-7 text-blue-300" aria-hidden="true" />
        <p className="text-2xl uppercase tracking-widest text-blue-300 font-semibold">
          {t('placement.placeIn')}
        </p>
      </div>
      <p
        className="text-8xl sm:text-9xl font-black text-white animate-glow-blue"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {binLabel}
      </p>
    </div>
  );
}

// BLU-197: bench placement — same labelled-spot pattern as a bin, distinct indigo hue.
function BenchPlacementState({ benchLabel }: { benchLabel: string }) {
  const { t } = useTranslation('production');
  return (
    <div className="text-center space-y-3">
      <div className="flex items-center justify-center gap-3">
        <LayoutPanelTop className="h-7 w-7 text-indigo-300" aria-hidden="true" />
        <p className="text-2xl uppercase tracking-widest text-indigo-300 font-semibold">
          {t('placement.placeOnBench')}
        </p>
      </div>
      <p
        className="text-8xl sm:text-9xl font-black text-white animate-glow-blue"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {benchLabel}
      </p>
    </div>
  );
}

function ShelfPlacementState() {
  const { t } = useTranslation('production');
  return (
    <div className="text-center space-y-3">
      <div className="flex items-center justify-center gap-3">
        <Package className="h-7 w-7 text-green-300" aria-hidden="true" />
        <p className="text-2xl uppercase tracking-widest text-green-300 font-semibold">
          {t('placement.placeOn')}
        </p>
      </div>
      <p className="text-7xl sm:text-8xl font-black text-white animate-glow-green">
        {t('placement.packingBench')}
      </p>
    </div>
  );
}
